import React, { useState, useEffect } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { searchExams, Exam } from '../services/examService';
import { auth, storage } from '../services/firebase';
import { initExamUpload, processExamUpload, extractStructuredExam, selectExamUploadCargo } from '../services/examIngestionService';

// ==================== TIPOS ====================

export interface OnboardingData {
  // Passo 1: Concurso
  targetExam: string;
  selectedExamId?: string; // ID do edital real
  selectedCargo?: string;
  uploadId?: string;
  extractedSubjects?: { name: string; topics: string[]; confidence?: string }[];
  targetBanca: string;
  targetArea: string;
  
  // Passo 2: Capacidade de Estudo
  hoursPerDay: number;
  daysPerWeek: number;
  
  // Campos mantidos para compatibilidade com o serviço, mas preenchidos automaticamente
  baseLevel: {
    portugues: 'fraco' | 'medio' | 'forte';
    raciocinio: 'fraco' | 'medio' | 'forte';
    direito: 'fraco' | 'medio' | 'forte';
  };
  baseLevelSkipped: boolean;
  studyTime: string;
  majorDifficulty: string;
  maturitySkipped: boolean;
  
  // Passo 3 (Condicional): Prazo
  targetDate: string;
  hasNoDate: boolean;
}

interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
  onBack: () => void;
  onSkip?: () => void;
  initialData?: Partial<OnboardingData>;
  onStepChange?: (step: number, data: Partial<OnboardingData>) => void;
}

// ==================== COMPONENTE PRINCIPAL ====================

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ 
  onComplete, 
  onBack,
  onSkip,
  initialData,
  onStepChange 
}) => {
  const toInputDate = (value: any): string => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    const br = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (br) {
      const dd = br[1].padStart(2, '0');
      const mm = br[2].padStart(2, '0');
      const yyyy = br[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return '';
  };

  const isReasonableExamDate = (isoDate: string, metadataYear?: number | null): boolean => {
    if (!isoDate) return false;
    const d = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return false;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const minDate = new Date(today);
    minDate.setFullYear(minDate.getFullYear() - 1); // tolerância mínima
    const maxDate = new Date(today);
    maxDate.setFullYear(maxDate.getFullYear() + 5);

    if (d < minDate || d > maxDate) return false;

    const y = d.getFullYear();
    if (typeof metadataYear === 'number' && metadataYear > 0) {
      if (y < metadataYear - 1 || y > metadataYear + 2) return false;
    }

    return true;
  };
  const [currentStep, setCurrentStep] = useState(1);
  
  // Estado inicial com valores padrão para os campos removidos da UI
  const initialState: OnboardingData = {
    targetExam: initialData?.targetExam || '',
    targetBanca: initialData?.targetBanca || '',
    targetArea: initialData?.targetArea || '',
    hoursPerDay: initialData?.hoursPerDay || 2,
    daysPerWeek: initialData?.daysPerWeek || 5,
    // Preenchimento automático para campos legados
    baseLevel: { portugues: 'medio', raciocinio: 'medio', direito: 'medio' },
    baseLevelSkipped: true,
    studyTime: '0',
    majorDifficulty: '',
    maturitySkipped: true,
    targetDate: initialData?.targetDate || '',
    hasNoDate: initialData?.hasNoDate || false
  };

  const [data, setData] = useState<OnboardingData>(initialState);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showExamList, setShowExamList] = useState(false);
  const [searchResults, setSearchResults] = useState<Exam[]>([]);
  const [searching, setSearching] = useState(false);
  const [examInputMode, setExamInputMode] = useState<'catalog' | 'pdf'>('catalog');
  const [pdfProcessing, setPdfProcessing] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [detectedCargos, setDetectedCargos] = useState<string[]>([]);
  const [pdfStage, setPdfStage] = useState<'idle' | 'upload' | 'process' | 'extract' | 'diagnosis' | 'done' | 'error'>('idle');
  const [pdfStats, setPdfStats] = useState<{ pages?: number; subjects?: number }>({});
  const [pdfWarning, setPdfWarning] = useState('');
  const [pdfQualityScore, setPdfQualityScore] = useState<number | null>(null);
  const [pdfQualityGate, setPdfQualityGate] = useState<{ passed?: boolean; reasons?: string[] } | null>(null);
  const [reviewedSubjects, setReviewedSubjects] = useState<{ name: string; topics: string[]; confidence?: string; enabled: boolean }[]>([]);

  const getDefaultSubjectsForCargo = (cargo?: string) => {
    const c = String(cargo || '').toLowerCase();
    if (c.includes('agente administrativo')) {
      return [
        'Língua Portuguesa',
        'Conhecimentos em Informática',
        'Matemática',
        'Raciocínio Lógico',
        'Atualidades',
        'Conhecimentos Gerais e Legislação Aplicada ao Município',
      ];
    }
    return [] as string[];
  };

  const toKey = (text: string = '') =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const sanitizeDetectedCargos = (list: string[]) => {
    const blocked = ['sala', 'prova', 'ata', 'equipe', 'conteudos', 'conteúdo', 'nivel superior conteudos'];
    const allowHints = /(agente|fiscal|assistente|tecnico|técnico|enfermagem|radiologia|motorista|porteiro|psicologo|psicopedagogo|engenheiro|farmaceutico|farmacêutico|odontologo|odontólogo|zootecnista|mecanico|mecânico|operador|professor)/i;

    const cleaned = list
      .map((x) => String(x || '').trim().replace(/\s+/g, ' '))
      .filter(Boolean)
      .filter((x) => x.length >= 4 && x.length <= 60)
      .filter((x) => !blocked.some((b) => toKey(x).includes(toKey(b))))
      .filter((x) => allowHints.test(x));

    return Array.from(new Set(cleaned));
  };

  const sanitizeSubjectsForCargo = (subjects: { name: string; topics: string[]; confidence?: string; enabled?: boolean }[], cargo?: string) => {
    const defaults = getDefaultSubjectsForCargo(cargo);
    if (defaults.length > 0) {
      const defaultKeys = defaults.map((d) => toKey(d));
      const mapped = subjects
        .map((s) => ({ ...s, name: String(s?.name || '').trim() }))
        .filter((s) => s.name)
        .filter((s) => {
          const k = toKey(s.name);
          return defaultKeys.some((dk) => k === dk || k.includes(dk) || dk.includes(k));
        });

      const finalList = mapped.length > 0
        ? mapped.map((s) => ({ ...s, enabled: s.enabled ?? true }))
        : defaults.map((name) => ({ name, topics: [], confidence: undefined, enabled: true }));

      return Array.from(new Map(finalList.map((s) => [toKey(s.name), s])).values());
    }

    return subjects
      .map((s) => ({ ...s, name: String(s?.name || '').trim(), enabled: s.enabled ?? true }))
      .filter((s) => s.name && s.name.length >= 3 && s.name.length <= 80);
  };

  const totalSteps = 6;
  const weekdayLabels = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  const [selectedStudyDays, setSelectedStudyDays] = useState<number[]>(
    Array.from({ length: Math.max(1, Math.min(7, Number(initialData?.daysPerWeek || 5))) }, (_, i) => i)
  );
  // Busca de editais reais
  useEffect(() => {
    const doSearch = async () => {
      setSearching(true);
      try {
        const term = searchTerm.trim();
        const results = await searchExams(term);
        // Sem termo: mostra apenas os mais recentes para não poluir
        setSearchResults(term ? results : results.slice(0, 5));
      } catch (error) {
        console.error("Erro na busca de editais:", error);
      } finally {
        setSearching(false);
      }
    };

    const timeoutId = setTimeout(doSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Notifica mudança de passo
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep, data);
    }
  }, [currentStep, data, onStepChange]);

  useEffect(() => {
    const daysCount = Math.max(1, selectedStudyDays.length);
    if (data.daysPerWeek !== daysCount) {
      setData((prev) => ({ ...prev, daysPerWeek: daysCount }));
    }
  }, [selectedStudyDays]);

  const weeklyHours = data.hoursPerDay * Math.max(1, selectedStudyDays.length);

  const handleExamSelect = (exam: Exam) => {
    const fullName = `${exam.metadata.institution} - ${exam.metadata.role} (${exam.metadata.year})`;
    const parsedExamDate = toInputDate(exam.metadata.examDate || '');
    const examDate = isReasonableExamDate(parsedExamDate, Number(exam.metadata.year || 0) || null) ? parsedExamDate : '';
    
    setData({ 
      ...data, 
      targetExam: fullName,
      selectedExamId: exam.id,
      targetBanca: exam.metadata.banca,
      targetArea: 'Geral',
      targetDate: examDate // Preenche a data automaticamente se existir
    });
    setSearchTerm(fullName);
    setShowExamList(false);
  };

  const handlePdfUpload = async (file: File) => {
    setPdfProcessing(true);
    setPdfError('');
    setDetectedCargos([]);
    setPdfStats({});
    setPdfWarning('');
    setPdfQualityScore(null);
    setPdfQualityGate(null);
    setPdfStage('upload');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Faça login para importar edital em PDF.');
      if (file.type !== 'application/pdf') throw new Error('Apenas PDF é permitido.');

      const storagePath = `exam-uploads/${user.uid}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      await uploadBytes(ref(storage, storagePath), file);

      const init = await initExamUpload({ userId: user.uid, filePath: storagePath, fileName: file.name, fileSize: file.size });

      setPdfStage('process');
      const processed = await processExamUpload(init.uploadId);
      setPdfStats((prev) => ({ ...prev, pages: processed?.pages }));

      setPdfStage('extract');
      const structured = await extractStructuredExam({ uploadId: init.uploadId, versionId: processed.versionId });
      setPdfQualityScore(typeof structured?.qualityScore?.score === 'number' ? structured.qualityScore.score : null);
      setPdfQualityGate(structured?.qualityGate || null);
      if (structured?.reviewRequired) {
        const reasons = Array.isArray(structured?.reviewReasons) ? structured.reviewReasons.slice(0, 2) : [];
        setPdfWarning(`Atenção: detectamos baixa confiança em alguns campos do edital${reasons.length ? ` (${reasons.join('; ')})` : ''}. Revise cargos e matérias antes de continuar.`);
      }

      const cargos = Array.isArray(structured?.cargos)
        ? structured.cargos.map((c: any) => String(c?.name || '').trim()).filter(Boolean)
        : [];

      const fallbackCargo = String(structured?.metadata?.role || '').trim();
      const rawCargos = cargos.length > 0 ? cargos : (fallbackCargo ? [fallbackCargo] : ['Cargo principal']);
      const safeCargos = sanitizeDetectedCargos(rawCargos);

      setPdfStage('diagnosis');
      const normalizedCargos = safeCargos.length > 0 ? safeCargos : ['Agente Administrativo'];
      setDetectedCargos(normalizedCargos);
      setPdfStats((prev) => ({ ...prev, subjects: Number(structured?.subjects || 0) }));
      const parsedDate = toInputDate(structured?.metadata?.examDate);
      const metadataYear = Number(structured?.metadata?.year || 0) || null;
      const validDate = parsedDate && isReasonableExamDate(parsedDate, metadataYear) ? parsedDate : '';
      if (parsedDate && !validDate) {
        setPdfWarning((prev) => {
          const dateMsg = 'Data da prova detectada parece inconsistente com o edital. Informe manualmente para gerar o plano correto.';
          return prev ? `${prev} ${dateMsg}` : dateMsg;
        });
      }

      const normalizedSubjects = Array.isArray(structured?.subjectsList)
        ? structured.subjectsList.map((s: any) => ({
            name: String(s?.name || '').trim(),
            topics: Array.isArray(s?.topics) ? s.topics.map((t: any) => String(t).trim()).filter(Boolean).slice(0, 20) : [],
            confidence: typeof s?.confidence === 'string' ? s.confidence : undefined,
          })).filter((s: any) => s.name)
        : [];

      const primaryCargo = normalizedCargos[0] || String(structured?.metadata?.role || '');
      const fallbackFromRole = getDefaultSubjectsForCargo(primaryCargo)
        .map((name) => ({ name, topics: [], confidence: undefined }));
      const reviewedBase = normalizedSubjects.length > 0 ? normalizedSubjects : fallbackFromRole;
      setReviewedSubjects(sanitizeSubjectsForCargo(reviewedBase.map((s: any) => ({ ...s, enabled: true })), primaryCargo));

      setData((prev) => ({
        ...prev,
        uploadId: init.uploadId,
        extractedSubjects: normalizedSubjects,
        targetExam: (structured?.metadata?.institution || prev.targetExam || file.name),
        targetBanca: structured?.metadata?.banca || prev.targetBanca || '',
        targetDate: validDate || prev.targetDate || '',
      }));

      setPdfStage('done');
    } catch (err: any) {
      setPdfStage('error');
      setPdfError(err?.message || 'Falha ao processar PDF.');
    } finally {
      setPdfProcessing(false);
    }
  };

  const handleCargoSelect = async (cargo: string) => {
    setData((prev) => {
      const base = (prev.targetExam || '').replace(/\s-\s[^-]+$/, '');
      return { ...prev, selectedCargo: cargo, targetExam: `${base} - ${cargo}` };
    });

    setReviewedSubjects((prev) => {
      const base = prev.length > 0
        ? prev
        : getDefaultSubjectsForCargo(cargo).map((name) => ({ name, topics: [], confidence: undefined, enabled: true }));
      return sanitizeSubjectsForCargo(base, cargo);
    });

    if (data.uploadId) {
      try {
        await selectExamUploadCargo({ uploadId: data.uploadId, selectedCargo: cargo });
      } catch (_) {}
    }
  };

  const toggleReviewedSubject = (index: number) => {
    setReviewedSubjects((prev) => prev.map((s, i) => i === index ? { ...s, enabled: !s.enabled } : s));
  };

  const updateReviewedSubjectName = (index: number, name: string) => {
    setReviewedSubjects((prev) => prev.map((s, i) => i === index ? { ...s, name } : s));
  };

  const updateReviewedSubjectTopics = (index: number, raw: string) => {
    const topics = String(raw || '')
      .split(/[\n,;|]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20);
    setReviewedSubjects((prev) => prev.map((s, i) => i === index ? { ...s, topics } : s));
  };

  const removeReviewedSubject = (index: number) => {
    setReviewedSubjects((prev) => prev.filter((_, i) => i !== index));
  };

  const addReviewedSubject = () => {
    setReviewedSubjects((prev) => ([...prev, { name: 'Nova matéria', topics: [], confidence: undefined, enabled: true }]));
  };

  useEffect(() => {
    if (examInputMode !== 'pdf') return;
    const cleaned = sanitizeSubjectsForCargo(reviewedSubjects, data.selectedCargo || '');
    const selected = cleaned.filter((s) => s.enabled).map(({ enabled, ...rest }) => rest);
    setData((prev) => ({ ...prev, extractedSubjects: selected }));
  }, [reviewedSubjects, examInputMode, data.selectedCargo]);

  const handleNext = () => {
    const isPdfFlow = examInputMode === 'pdf';

    if (currentStep === 1) {
      if (!isPdfFlow && !data.targetExam && searchTerm.trim().length >= 3) {
        setData((prev) => ({ ...prev, targetExam: searchTerm.trim() }));
      }
      if (isPdfFlow && !data.targetExam) {
        setData((prev) => ({ ...prev, targetExam: prev.selectedCargo ? `Edital enviado - ${prev.selectedCargo}` : 'Edital enviado em PDF' }));
      }
      setCurrentStep(isPdfFlow ? 2 : 4);
      return;
    }
    if (currentStep === 2) {
      setCurrentStep(3);
      return;
    }
    if (currentStep === 3) {
      setCurrentStep(4);
      return;
    }
    if (currentStep === 4) {
      setCurrentStep(5);
      return;
    }
    if (currentStep === 5) {
      setCurrentStep(6);
      return;
    }
    if (currentStep === 6) {
      onComplete({ ...data, daysPerWeek: Math.max(1, selectedStudyDays.length) } as OnboardingData);
      return;
    }
  };

  const handlePrevious = () => {
    const isPdfFlow = examInputMode === 'pdf';
    if (currentStep === 1) {
      onBack();
      return;
    }
    if (!isPdfFlow && currentStep === 4) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleReset = () => {
    setData(initialState);
    setCurrentStep(1);
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
      return;
    }
    onBack();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (examInputMode === 'catalog') return data.targetExam.length > 0 || searchTerm.trim().length >= 3;
        return data.targetExam.length > 0 || !!data.uploadId || pdfStage === 'done';
      case 2:
        return examInputMode !== 'pdf' || !!data.selectedCargo;
      case 3:
        return examInputMode !== 'pdf' || reviewedSubjects.filter((s) => s.enabled && String(s.name || '').trim().length > 0).length >= 5;
      case 4:
        return data.targetDate.length > 0 || data.hasNoDate;
      case 5:
        return data.hoursPerDay >= 1 && selectedStudyDays.length >= 1;
      case 6:
        return true;
      default:
        return false;
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const progressPct = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-white/85 backdrop-blur-xl px-6 py-5 flex justify-between items-center sticky top-0 z-50 border-b border-white/60 shadow-[0_8px_30px_rgb(15,23,42,0.05)]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-white font-black shadow-emerald-200 shadow-lg">
            CP
          </div>
          <div>
            <span className="font-black text-gray-900 tracking-tight">Criar Plano de Estudos</span>
            <p className="text-[11px] text-gray-500 -mt-0.5">Onboarding inteligente</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg font-semibold text-sm border border-emerald-100 hover:bg-emerald-100 transition"
          >
            Refazer plano
          </button>
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-emerald-600 font-semibold text-sm underline decoration-dotted"
            title="Criar depois"
          >
            Criar depois
          </button>
        </div>
      </header>

      {/* Progress */}
      <div className="px-6 pt-4">
        <div className="max-w-3xl mx-auto bg-white/80 border border-white rounded-2xl px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-black text-gray-500 uppercase tracking-[0.12em]">Passo {currentStep} de {totalSteps}</p>
            <p className="text-xs font-black text-emerald-700">{progressPct}%</p>
          </div>
          <div className="h-2 bg-gray-100 w-full mt-2 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-700 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <main className="flex-1 flex flex-col items-stretch p-6 max-w-3xl mx-auto w-full animate-fade-in gap-6 md:gap-8">
        {/* ==================== PASSO 1: O QUE (CONCURSO) ==================== */}
        {currentStep === 1 && (
          <div className="w-full space-y-4 md:space-y-5">
            <div className="space-y-1 bg-white/90 border border-white rounded-2xl p-4 shadow-sm">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight text-left">Qual edital você quer?</h1>
              <p className="text-gray-500 text-sm md:text-base">Você pode buscar na base ou importar PDF.</p>
            </div>

            <div className="flex gap-2 bg-white p-1 rounded-2xl border border-gray-100 shadow-sm">
              <button onClick={() => setExamInputMode('catalog')} className={`px-4 py-2 rounded-xl text-sm font-bold transition ${examInputMode === 'catalog' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow' : 'bg-white text-gray-700'}`}>Base de editais</button>
              <button onClick={() => setExamInputMode('pdf')} className={`px-4 py-2 rounded-xl text-sm font-bold transition ${examInputMode === 'pdf' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow' : 'bg-white text-gray-700'}`}>Importar PDF</button>
            </div>

            {examInputMode === 'catalog' && <div className="relative group">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              <input
                type="text"
                autoFocus
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (data.targetExam && e.target.value !== data.targetExam) {
                     setData({ ...data, targetExam: '', selectedExamId: undefined, targetBanca: '', targetDate: '' });
                  }
                  setShowExamList(true);
                }}
                onFocus={() => setShowExamList(true)}
                placeholder="Ex: TJ-SP escrevente, INSS técnico, PF administrativo..."
                className="w-full bg-white border-2 border-emerald-200 focus:border-emerald-500 rounded-2xl py-4 pl-12 pr-4 text-base md:text-lg font-semibold shadow-sm transition-all outline-none"
              />
              
              {showExamList && (
                <div className="absolute z-20 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 max-h-[260px] overflow-y-auto custom-scrollbar">
                  {searching ? (
                    <div className="p-6 text-center text-gray-400">Buscando oportunidades...</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((exam) => {
                      const normalizedTerm = searchTerm.trim().toLowerCase();
                      const aliasList = exam.metadata.aliases || [];
                      const aliasMatch = normalizedTerm.length > 0 && aliasList.some((a) => a.toLowerCase().includes(normalizedTerm));

                      return (
                      <button
                        key={exam.id}
                        onClick={() => handleExamSelect(exam)}
                        className="w-full text-left px-4 py-3 hover:bg-emerald-50/60 transition-colors border-b border-gray-50 last:border-0 group"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <span className="font-bold text-gray-800 block text-sm md:text-base group-hover:text-emerald-700 transition-colors truncate">
                              {exam.metadata.institution}
                            </span>
                            <span className="text-xs md:text-sm text-gray-600 block truncate">
                              {exam.metadata.role}
                            </span>
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-bold">
                                {exam.metadata.year}
                              </span>
                              <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-100">
                                {exam.metadata.banca || 'Sem banca'}
                              </span>
                              {aliasMatch && (
                                <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-bold border border-amber-100">
                                  Apelido
                                </span>
                              )}
                              {exam.metadata.examDate ? (
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                                  Com data
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <span className="text-emerald-500 font-black text-base">→</span>
                        </div>
                      </button>
                    )})
                  ) : (
                    <div className="p-6 text-center">
                      <p className="text-gray-500 mb-3">Não encontramos esse edital específico.</p>
                      <button
                        onClick={() => {
                          setData({ ...data, targetExam: searchTerm, selectedExamId: undefined, targetDate: '' });
                          setShowExamList(false);
                        }}
                        className="text-emerald-600 font-bold hover:text-emerald-700 hover:underline"
                      >
                        Usar "{searchTerm}" assim mesmo
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>}

            {examInputMode === 'pdf' && (
              <div className="space-y-3 bg-white/95 border border-gray-200 rounded-2xl p-4 shadow-sm">
                <p className="text-sm text-gray-600 font-semibold">Envie o edital em PDF. Vamos detectar os cargos automaticamente.</p>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handlePdfUpload(f);
                  }}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm"
                />
                {(pdfProcessing || pdfStage === 'done' || pdfStage === 'error') && (
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
                    <p className="text-xs font-black uppercase tracking-[0.12em] text-emerald-700">Leitura do edital por IA</p>
                    {[
                      { key: 'upload', label: 'Upload do PDF' },
                      { key: 'process', label: 'Extração de texto' },
                      { key: 'extract', label: 'Análise da IA' },
                      { key: 'diagnosis', label: 'Montagem do diagnóstico' },
                    ].map((step, idx) => {
                      const order: Record<string, number> = { idle: -1, upload: 0, process: 1, extract: 2, diagnosis: 3, done: 4, error: 4 };
                      const current = order[pdfStage] ?? -1;
                      const isDone = current > idx || pdfStage === 'done';
                      const isCurrent = current === idx && pdfStage !== 'done' && pdfStage !== 'error';

                      return (
                        <div key={step.key} className="flex items-center gap-2 text-sm">
                          <span className={`inline-flex w-5 h-5 items-center justify-center rounded-full text-xs font-black ${isDone ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isDone ? '✓' : isCurrent ? '…' : idx + 1}
                          </span>
                          <span className={`${isDone ? 'text-emerald-700 font-semibold' : isCurrent ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>{step.label}</span>
                        </div>
                      );
                    })}

                    {(pdfStats.pages || pdfStats.subjects) && (
                      <p className="text-xs text-gray-600">
                        {pdfStats.pages ? `${pdfStats.pages} páginas lidas` : ''}
                        {pdfStats.pages && pdfStats.subjects ? ' • ' : ''}
                        {pdfStats.subjects ? `${pdfStats.subjects} matérias detectadas` : ''}
                      </p>
                    )}
                  </div>
                )}

                {pdfError && <p className="text-sm text-red-600 font-semibold">{pdfError}</p>}
                {pdfWarning && <p className="text-sm text-amber-700 font-semibold">{pdfWarning}</p>}
                {pdfQualityScore !== null && (
                  <p className="text-xs text-gray-700 font-semibold">Qualidade da extração: {pdfQualityScore}/100 {pdfQualityGate?.passed ? '• gate PASS' : '• gate FAIL'}</p>
                )}
                {pdfQualityGate && Array.isArray(pdfQualityGate.reasons) && pdfQualityGate.reasons.length > 0 && (
                  <p className="text-xs text-amber-700">{pdfQualityGate.reasons.join(' • ')}</p>
                )}

                {pdfStage === 'done' && <p className="text-xs text-emerald-700 font-semibold">Upload concluído. Clique em Continuar para escolher o cargo.</p>}
              </div>
            )}

            {data.targetExam && (
              <div className="bg-emerald-50/80 border border-emerald-100 p-4 rounded-2xl flex items-center gap-4 animate-slide-up">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-xl shadow-sm">✅</div>
                <div>
                  <p className="font-bold text-emerald-900 text-lg">{data.targetExam}</p>
                  {data.targetDate ? (
                    <p className="text-sm text-emerald-600 font-medium">Data da prova: {new Date(data.targetDate).toLocaleDateString('pt-BR')}</p>
                  ) : (
                    <p className="text-sm text-emerald-600">Sem data definida na base</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== PASSO 2: CARGO (somente upload PDF) ==================== */}
        {currentStep === 2 && examInputMode === 'pdf' && (
          <div className="w-full space-y-6 animate-fade-in">
            <div className="space-y-1 bg-white/90 border border-white rounded-2xl p-4 shadow-sm">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Escolha seu cargo</h1>
              <p className="text-gray-500 text-sm md:text-base">Com o cargo definido, o plano fica muito mais fiel ao edital.</p>
            </div>
            <div className="bg-white/95 border border-gray-200 rounded-2xl p-4 space-y-3 shadow-sm">
              {detectedCargos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detectedCargos.map((cargo) => (
                    <button
                      key={cargo}
                      onClick={() => handleCargoSelect(cargo)}
                      className={`text-left px-3 py-2 rounded-lg border text-sm font-semibold ${data.selectedCargo === cargo ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-gray-200 text-gray-700'}`}
                    >
                      {cargo}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-700">Ainda não encontramos cargos. Volte e reenvie o PDF.</p>
              )}
            </div>
          </div>
        )}

        {/* ==================== PASSO 3: MATÉRIAS (somente upload PDF) ==================== */}
        {currentStep === 3 && examInputMode === 'pdf' && (
          <div className="w-full space-y-6 animate-fade-in">
            <div className="space-y-1 bg-white/90 border border-white rounded-2xl p-4 shadow-sm">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Confirme as matérias</h1>
              <p className="text-gray-500 text-sm md:text-base">Desmarque o que não pertence ao cargo selecionado.</p>
            </div>
            <div className="space-y-3 border border-gray-200 rounded-xl p-3 bg-white/95 shadow-sm">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Validação do edital (campos críticos)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  value={data.selectedCargo || ''}
                  onChange={(e) => setData((prev) => ({ ...prev, selectedCargo: e.target.value }))}
                  placeholder="Cargo alvo"
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
                <input
                  value={data.targetBanca || ''}
                  onChange={(e) => setData((prev) => ({ ...prev, targetBanca: e.target.value }))}
                  placeholder="Banca (ex: Cebraspe)"
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
                <input
                  type="date"
                  value={data.targetDate || ''}
                  min={today}
                  max={maxDateStr}
                  onChange={(e) => setData((prev) => ({ ...prev, targetDate: e.target.value, hasNoDate: false }))}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
              </div>

              {reviewedSubjects.length > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">Selecionadas: {reviewedSubjects.filter((s) => s.enabled && String(s.name || '').trim().length > 0).length} (mínimo 5 para continuar)</p>
                    <button onClick={addReviewedSubject} className="text-xs font-bold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">+ Adicionar matéria</button>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {reviewedSubjects.map((subject, idx) => (
                      <div key={`${subject.name}-${idx}`} className={`rounded-lg border p-2 ${subject.enabled ? 'bg-white border-emerald-200' : 'bg-white border-gray-200 opacity-80'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <input type="checkbox" checked={subject.enabled} onChange={() => toggleReviewedSubject(idx)} />
                          <input
                            value={subject.name}
                            onChange={(e) => updateReviewedSubjectName(idx, e.target.value)}
                            className="flex-1 px-2 py-1.5 rounded-md border border-gray-200 text-sm"
                            placeholder="Nome da matéria"
                          />
                          <button onClick={() => removeReviewedSubject(idx)} className="px-2 py-1.5 rounded-md text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">Remover</button>
                        </div>
                        <textarea
                          value={Array.isArray(subject.topics) ? subject.topics.join(', ') : ''}
                          onChange={(e) => updateReviewedSubjectTopics(idx, e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                          placeholder="Tópicos (separar por vírgula ou quebra de linha)"
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-amber-700">Ainda não foi possível montar a lista de matérias para esse cargo.</p>
                  <button onClick={addReviewedSubject} className="text-xs font-bold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">Adicionar matéria manualmente</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== PASSO 5: QUANTO (TEMPO) ==================== */}
        {currentStep === 5 && (
          <div className="w-full space-y-10 animate-fade-in">
            <div className="space-y-2 text-center bg-white/90 border border-white rounded-2xl p-5 shadow-sm">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">Passo 5: Definindo sua rotina possível ⏰</h1>
              <p className="text-gray-500 text-lg">Seja realista. Isso serve para ajustar seu plano ao seu tempo real.</p>
            </div>

            <div className="bg-white/95 p-8 rounded-3xl shadow-sm border border-gray-100 space-y-8">
              {/* Horas */}
              <div className="space-y-4">
                <div className="flex justify-between items-end">
                  <label className="font-bold text-gray-700">Horas por dia</label>
                  <span className="text-4xl font-black text-emerald-500">{data.hoursPerDay}h</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="12"
                  value={data.hoursPerDay}
                  onChange={(e) => setData({ ...data, hoursPerDay: parseInt(e.target.value) })}
                  className="w-full h-4 bg-gray-100 rounded-full appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-400 transition-all"
                />
                <div className="flex justify-between text-xs text-gray-400 font-medium">
                  <span>Leve (1h)</span>
                  <span>Insano (12h)</span>
                </div>
              </div>

              {/* Dias */}
              <div className="space-y-4">
                <label className="font-bold text-gray-700 block">Dias da semana que você vai estudar</label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {weekdayLabels.map((label, idx) => {
                    const active = selectedStudyDays.includes(idx);
                    return (
                      <button
                        key={label}
                        onClick={() => {
                          setSelectedStudyDays((prev) => {
                            const has = prev.includes(idx);
                            if (has) return prev.filter((d) => d !== idx);
                            return [...prev, idx].sort((a, b) => a - b);
                          });
                        }}
                        className={`rounded-xl py-3 font-black text-sm transition-all ${active ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-center text-sm text-gray-500 font-medium bg-gray-50 py-2 rounded-lg">
                  Dias selecionados: <span className="text-emerald-600 font-bold">{Math.max(1, selectedStudyDays.length)}</span> • Total estimado: <span className="text-emerald-600 font-bold">{weeklyHours} horas</span>/semana
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== PASSO 4: DATA DA PROVA ==================== */}
        {currentStep === 4 && (
          <div className="w-full space-y-8 animate-fade-in">
            <div className="space-y-2 text-center bg-white/90 border border-white rounded-2xl p-5 shadow-sm">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">Passo 4: Definindo o prazo do plano 📅</h1>
              <p className="text-gray-500 text-lg">Se não souber a data da prova, sem problema: criamos um plano base e você ajusta depois.</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => setData({ ...data, hasNoDate: !data.hasNoDate, targetDate: '' })}
                className={`w-full p-5 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                  data.hasNoDate
                    ? 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
                    : 'border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  data.hasNoDate ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                }`}>
                  {data.hasNoDate && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  )}
                </div>
                <div className="text-left">
                  <span className={`font-bold text-lg ${data.hasNoDate ? 'text-emerald-700' : 'text-gray-700'}`}>
                    Ainda sem data definida
                  </span>
                  <p className="text-sm text-gray-500">Vamos focar em construir sua base (12 semanas)</p>
                </div>
              </button>

              <div className={`transition-all duration-300 ${data.hasNoDate ? 'opacity-50 pointer-events-none grayscale' : 'opacity-100'}`}>
                <label className="block text-sm font-bold text-gray-700 mb-2 pl-1">Ou selecione no calendário:</label>
                <input
                  type="date"
                  value={data.targetDate}
                  min={today}
                  max={maxDateStr}
                  onChange={(e) => setData({ ...data, targetDate: e.target.value, hasNoDate: false })}
                  className="w-full bg-white border-2 border-gray-100 rounded-2xl px-5 py-4 text-xl text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* PASSO 6: CONFIRMAÇÃO */}
        {currentStep === 6 && (
          <div className="space-y-4">
            <div className="rounded-3xl p-5 md:p-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-xl shadow-emerald-200/60">
              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-emerald-100">Passo final</p>
              <h2 className="text-2xl md:text-3xl font-black mt-1">Seu plano está pronto para nascer 🚀</h2>
              <p className="text-emerald-50 text-sm mt-2">Revise os dados abaixo. Se estiver tudo certo, confirme e gere seu plano personalizado.</p>
            </div>

            <div className="bg-white/95 border border-white rounded-3xl p-4 md:p-5 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-black">Concurso</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{data.targetExam || "Não definido"}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-black">Cargo</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{data.selectedCargo || "Não definido"}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-black">Banca</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{data.targetBanca || "Não definida"}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-black">Prazo</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{data.hasNoDate ? "Sem data definida" : (data.targetDate || "Não definido")}</p>
                </div>
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-emerald-700 font-black">Horas por dia</p>
                  <p className="text-lg font-black text-emerald-800 mt-1">{data.hoursPerDay}h</p>
                </div>
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-emerald-700 font-black">Dias por semana</p>
                  <p className="text-lg font-black text-emerald-800 mt-1">{Math.max(1, selectedStudyDays.length)}</p>
                </div>
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-emerald-700 font-black">Matérias confirmadas</p>
                  <p className="text-lg font-black text-emerald-800 mt-1">{reviewedSubjects.filter((s) => s.enabled).length || (data.extractedSubjects?.length || 0)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Se algo estiver incorreto, volte e ajuste. Ao confirmar, o plano será gerado.</p>
            </div>
          </div>
        )}

        {/* ==================== FOOTER / NAVEGAÇÃO ==================== */}
        <div className="fixed bottom-0 left-0 w-full bg-white/90 backdrop-blur-xl border-t border-white p-4 md:p-6 z-40 shadow-[0_-8px_30px_rgb(15,23,42,0.08)]">
          <div className="max-w-5xl mx-auto flex flex-col gap-2">
            {currentStep === 1 && !canProceed() && (
              <p className="text-xs text-gray-500 font-semibold">Selecione um edital (ou envie PDF e escolha o cargo) para continuar.</p>
            )}
            <div className="flex items-center gap-4">
            <button
              onClick={handleSkip}
              className="h-14 px-4 rounded-2xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold text-sm"
            >
              Criar depois
            </button>

            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="w-14 h-14 rounded-2xl bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex-1 h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md ${
                canProceed()
                  ? 'bg-emerald-500 text-white shadow-emerald-200'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              }`}
            >
              {currentStep === 6 ? 'Gerar meu plano de estudos 🚀' : 'Continuar'}
            </button>
          </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default OnboardingWizard;

